import { OnvoDashboardDatasources } from "../dashboard_datasources/index.js";
import OnvoBase, { JobUpdateEvent } from "../base.js";
import { Dashboard } from "../types/dashboard.js";
import { WidgetSuggestion } from "../types/index.js";
import { OnvoWidgetSuggestions } from "../widget_suggestions/index.js";

export class OnvoDashboard extends OnvoBase {
  datasources: OnvoDashboardDatasources;
  widgetSuggestions: OnvoWidgetSuggestions;
  #id: string;

  constructor(id: string, apiKey: string, options?: { endpoint: string }) {
    super(apiKey, options);
    this.#id = id;
    this.datasources = new OnvoDashboardDatasources(id, apiKey, options);
    this.widgetSuggestions = new OnvoWidgetSuggestions(id, apiKey, options);
  }

  /**
   * Updates the widget cache for the dashboard.
   *
   * @return {Promise<{success: boolean, id: string}>} A promise that resolves to an object containing the success and id of the widget.
   */
  updateCache(callback?: (event: JobUpdateEvent) => void, options?: {
    rejectCache?: boolean,
    rejectFilterCache?: boolean
  }) {
    const queryParams = [];
    if (options?.rejectCache) {
      queryParams.push("rejectCache=true");
    }
    if (options?.rejectFilterCache) {
      queryParams.push("rejectFilterCache=true");
    }

    let queryString = "";
    if (queryParams.length > 0) {
      queryString = "?" + queryParams.join("&");
    }

    return this._fetchStreamBase(
      `/api/dashboards/${this.#id}/update-cache${queryString}`,
      "POST",
      undefined,
      callback
    ) as Promise<Dashboard & { _meta: { status: string, url: string, total?: number, completed?: number } }>;
  }

  /**
   * Exports the dashboard in the specified format.
   *
   * @param {("csv" | "xlsx" | "pdf" | "png" | "jpeg")} format - The format to export the dashboard in.
   * @return {Promise<Blob>} A promise that resolves to a Blob representing the exported dashboard.
   */
  export(format: "csv" | "xlsx" | "pdf" | "png" | "jpeg" | "pptx", theme: "light" | "dark" = "light", tab: number = 0, onProgress?: (event: JobUpdateEvent) => void) {
    return this._fetchStreamBase(
      `/api/dashboards/${this.#id}/export?theme=${theme}&format=${format}&tab=${tab}`,
      "GET",
      undefined,
      onProgress
    );
  }

  /**
 * Duplicates the dashboard.
 *
 * @return {Promise<{id: string}>} A promise that resolves to an object containing the id of the duplicated dashboard.
 */
  duplicate(): Promise<{ id: string }> {
    return this._fetchBase(`/api/dashboards/${this.#id}/duplicate`, "POST");
  }

  /**
   * Summarizes the dashboard using the provided prompt.
   *
   * @param {string} prompt - The prompt to use for summarizing the dashboard.
   * @return {Promise<{title: string, description: string}>} A promise that resolves to an object containing the title and description of the summarized dashboard.
   */
  summarize(prompt: string): Promise<{ title: string; description: string }> {
    return this._fetchStreamBase(`/api/dashboards/${this.#id}/summarize`, "POST", {
      prompt,
    });
  }

  /**
   * Synchronizes the dashboard with the latest data and settings.
   *
   * @return {Promise<any>} A promise that resolves with the sync result
   */
  sync(): Promise<any> {
    return this._fetchBase(`/api/dashboards/${this.#id}/sync`, "POST");
  }

  generateWidgetSuggestions(): Promise<WidgetSuggestion[]> {
    return this._fetchStreamBase(`/api/dashboards/${this.#id}/generate-suggestions`, "POST");
  }
}